import React, {memo, ReactNode, useCallback, useMemo, useState} from 'react';

import './index.scss';
import iconRemove from '../../assets/images/icons/media/remove.png';
import iconCancel from '../../assets/images/icons/media/cancel.png';
import iconSave from '../../assets/images/icons/media/done.png';

import iconAirConditioner from '../../assets/images/icons/media/air-conditioner.png';
import iconEdit from '../../assets/images/icons/media/edit.png';
import iconOff from '../../assets/images/icons/media/off.png';
import iconOn from '../../assets/images/icons/media/on.png';
import iconOnOff from '../../assets/images/icons/media/on-off.png';
import iconF from '../../assets/images/icons/media/f1.png';
import iconAuto from '../../assets/images/icons/controls/air-conditioner-auto.png';
import iconFan from '../../assets/images/icons/controls/air-conditioner-fan.png';
import iconDry from '../../assets/images/icons/controls/air-conditioner-dry.png';
import iconCold from '../../assets/images/icons/controls/air-conditioner-cold.png';
import iconHot from '../../assets/images/icons/controls/air-conditioner-hot.png';

import iconFan0 from '../../assets/images/icons/controls/fan-0.png';
import iconFan1 from '../../assets/images/icons/controls/fan-1.png';
import iconFan2 from '../../assets/images/icons/controls/fan-2.png';
import iconFan3 from '../../assets/images/icons/controls/fan-3.png';
import iconFan4 from '../../assets/images/icons/controls/fan-4.png';

import iconFlap0 from '../../assets/images/icons/controls/air-flap-0.png';
import iconFlap1 from '../../assets/images/icons/controls/air-flap-1.png';
import iconFlap2 from '../../assets/images/icons/controls/air-flap-2.png';
import iconFlap3 from '../../assets/images/icons/controls/air-flap-3.png';
import iconFlap4 from '../../assets/images/icons/controls/air-flap-4.png';

import iconPlus from '../../assets/images/icons/media/plus.png';
import iconMinus from '../../assets/images/icons/media/minus.png';
import iconProgram from '../../assets/images/icons/media/program.png';
import iconVolume from '../../assets/images/icons/media/volume.png';
import iconMute from '../../assets/images/icons/media/mute.png';
import iconList from '../../assets/images/icons/media/list.png';
import iconMenu from '../../assets/images/icons/media/menu.png';


import iconPlay from '../../assets/images/icons/media/play.png';
import iconPause from '../../assets/images/icons/media/pause.png';
import iconNext from '../../assets/images/icons/media/next.png';
import iconRewind from '../../assets/images/icons/media/rewind.png';
import iconF1 from '../../assets/images/icons/media/f1.png';
import iconF2 from '../../assets/images/icons/media/f2.png';

import iconSettings from '../../assets/images/icons/media/settings.png';
import iconLeft from '../../assets/images/icons/media/left.png';
import iconBack from '../../assets/images/icons/media/back.png';
import iconUp from '../../assets/images/icons/media/up.png';
import iconOk from '../../assets/images/icons/media/ok.png';
import iconDown from '../../assets/images/icons/media/down.png';
import iconInput from '../../assets/images/icons/media/input.png';
import iconRight from '../../assets/images/icons/media/right.png';
import iconExit from '../../assets/images/icons/media/exit.png';

import {setMediaScreenAction} from '../../store/ui/actions';
import {useRPC} from '../../hooks/rpc';
import {useDispatch, useSelector} from 'react-redux';
import {useMessage} from '../../hooks/message';
import {useQuestion} from '../../hooks/question';
import {onClickHandler} from '../../utils/onclick';
import {MEDIA_TYPE_ID_AIR_CONDITIONER} from '../../types/control';
import {useRelations} from '../../hooks/relations';
import {mediaIcons} from '../MediaListScreen';
import {useLocalization} from "../../hooks/localization";

type MyButtonProps = {
    editMode: boolean;
    onClick?: () => void;
    slot?: number;
    children?: ReactNode;
};

const MyButton = memo(function MyButton(props: MyButtonProps) {
    const {t} = useLocalization();
    const selectedMedia = useSelector(state => state.ui.mediaScreen.selectedMedia);
    const [rpc] = useRPC();
    const question = useQuestion();

    const mediaButtonLookup = useMemo(() => {
        const lookup = {};
        if (selectedMedia) {
            for (const button of selectedMedia.button_list) {
                lookup[button.slot] = button;
            }
        }
        return lookup;
    }, [selectedMedia]);

    let {onClick} = props;

    let isRegistered = false;
    if (props.slot != null) {
        const button = mediaButtonLookup[props.slot];
        if (button) {
            isRegistered = button.registered;
        }
    }

    const click = useCallback(() => {
        let isRegistered = false;
        const button = mediaButtonLookup[props.slot];
        if (button) {
            isRegistered = button.registered;
        }
        if (props.editMode) {
            if (isRegistered) {
                question(t('DO_YOU_WANT_TO_DELETE_THE_SAVED_REGISTERED_KEY_INFORMATION'), () => {
                    rpc.mediaButtonPressed(selectedMedia.id, props.slot, {deletion: true});
                });
            } else {
                rpc.mediaButtonPressed(selectedMedia.id, props.slot, {registration: true});
            }
        } else {
            if (isRegistered) {
                rpc.mediaButtonPressed(selectedMedia.id, props.slot);
            }
        }
    }, [t, mediaButtonLookup, props.slot, props.editMode, question, rpc, selectedMedia.id]);

    if (props.slot != null) {
        onClick = click;
    }

    return (
        <div className={'button' + (isRegistered ? ' registered' : '') + (onClick ? '' : ' no-border')}
             {...onClickHandler(onClick)}>
            {props.children}
        </div>
    );
});

const MyIconButton = memo(function MyIconButton({icon, slot, editMode}: { icon: string; slot?: number; editMode: boolean; }) {
    return (
        <MyButton slot={slot} editMode={editMode}>
            <img src={icon} alt=""/>
        </MyButton>
    );
});

const MyTemperatureButton = memo(function MyTemperatureButton({text, slot, editMode}: { text: string; slot: number; editMode: boolean; }) {
    return (
        <MyButton slot={slot} editMode={editMode}>
            <div className="temperature">
                <span>{text}<small>°</small></span>
            </div>
        </MyButton>
    );
});

const MyTextButton = memo(function MyTextButton({text, slot, editMode}: { text: string; slot: number; editMode: boolean; }) {
    return (
        <MyButton slot={slot} editMode={editMode}>
            <span>{text}</span>
        </MyButton>
    );
});

const LeftAirConditionerParent = memo(function LeftAirConditionerParent({editMode}: { editMode: boolean }) {
    const selectedMedia = useSelector(state => state.ui.mediaScreen.selectedMedia);
    return (
        <div className="left-parent air-conditioner">
            <div className="left-top">
                <div className="left-top-left">
                    <div className="open-close-button">
                        <img src={iconAirConditioner} alt='icon'/>
                        <div className="open-close-button-title">
                            <span>{selectedMedia.name}</span>
                        </div>
                    </div>

                    <div className="left-top-left-bottom">
                        <MyIconButton icon={iconOff} slot={0} editMode={editMode}/>
                        <MyIconButton icon={iconOn} slot={1} editMode={editMode}/>
                    </div>
                </div>
                <div className="left-top-right">
                    <div>
                        <MyIconButton icon={iconAuto} slot={2} editMode={editMode}/>
                        <MyIconButton icon={iconFan} slot={3} editMode={editMode}/>
                        <MyIconButton icon={iconDry} slot={4} editMode={editMode}/>
                    </div>
                    <div>
                        <MyIconButton icon={iconCold} slot={5} editMode={editMode}/>
                        <MyIconButton icon={iconHot} slot={6} editMode={editMode}/>
                        <MyIconButton icon={iconF} slot={7} editMode={editMode}/>
                    </div>
                </div>
            </div>
            <div className="left-bottom">
                <div>
                    <MyIconButton icon={iconFan0} slot={8} editMode={editMode}/>
                    <MyIconButton icon={iconFan1} slot={9} editMode={editMode}/>
                    <MyIconButton icon={iconFan2} slot={10} editMode={editMode}/>
                    <MyIconButton icon={iconFan3} slot={11} editMode={editMode}/>
                    <MyIconButton icon={iconFan4} slot={12} editMode={editMode}/>
                </div>
                <div>
                    <MyIconButton icon={iconFlap0} slot={13} editMode={editMode}/>
                    <MyIconButton icon={iconFlap1} slot={14} editMode={editMode}/>
                    <MyIconButton icon={iconFlap2} slot={15} editMode={editMode}/>
                    <MyIconButton icon={iconFlap3} slot={16} editMode={editMode}/>
                    <MyIconButton icon={iconFlap4} slot={17} editMode={editMode}/>
                </div>
            </div>
        </div>
    );
});

const RightAirConditionerParent = memo(function RightAirConditionerParent({editMode}: { editMode: boolean }) {
    return (
        <div className="right-parent air-conditioner">
            <div className="right-column">
                <MyTemperatureButton text='15' slot={18} editMode={editMode}/>
                <MyTemperatureButton text='16' slot={19} editMode={editMode}/>
                <MyTemperatureButton text='17' slot={20} editMode={editMode}/>
                <MyTemperatureButton text='18' slot={21} editMode={editMode}/>
            </div>
            <div className="right-column">
                <MyTemperatureButton text='19' slot={22} editMode={editMode}/>
                <MyTemperatureButton text='20' slot={23} editMode={editMode}/>
                <MyTemperatureButton text='21' slot={24} editMode={editMode}/>
                <MyTemperatureButton text='22' slot={25} editMode={editMode}/>
            </div>
            <div className="right-column">
                <MyTemperatureButton text='23' slot={26} editMode={editMode}/>
                <MyTemperatureButton text='24' slot={27} editMode={editMode}/>
                <MyTemperatureButton text='25' slot={28} editMode={editMode}/>
                <MyTemperatureButton text='26' slot={29} editMode={editMode}/>
            </div>
            <div className="right-column">
                <MyTemperatureButton text='27' slot={30} editMode={editMode}/>
                <MyTemperatureButton text='28' slot={31} editMode={editMode}/>
                <MyTemperatureButton text='29' slot={32} editMode={editMode}/>
                <MyTemperatureButton text='30' slot={33} editMode={editMode}/>
            </div>
            <div className="right-column">
                <MyTemperatureButton text='31' slot={34} editMode={editMode}/>
                <MyTemperatureButton text='32' slot={35} editMode={editMode}/>
                <MyButton editMode={false}/>
                <MyButton editMode={false}/>
            </div>
        </div>
    );
});

const LeftTvParent = memo(function LeftTvParent({editMode}: { editMode: boolean }) {
    const selectedMedia = useSelector(state => state.ui.mediaScreen.selectedMedia);
    return (
        <div className="left-parent tv">
            <div className="left-top">
                <div className="left-top-left">
                    <div className="open-close-button">
                        <img src={mediaIcons[selectedMedia.icon]} alt='icon'/>
                        <div className="open-close-button-title">
                            <span>{selectedMedia.name}</span>
                        </div>
                    </div>
                    <div className="left-top-left-bottom">
                        <MyIconButton icon={iconOnOff} slot={0} editMode={editMode}/>
                    </div>
                </div>
                <div className="left-top-right">
                    <div>
                        <MyIconButton icon={iconPlus} slot={1} editMode={editMode}/>
                        <MyIconButton icon={iconProgram} editMode={false}/>
                        <MyIconButton icon={iconMinus} slot={2} editMode={editMode}/>
                    </div>
                    <div>
                        <MyIconButton icon={iconMute} slot={3} editMode={editMode}/>
                        <MyIconButton icon={iconList} slot={4} editMode={editMode}/>
                        <MyIconButton icon={iconMenu} slot={5} editMode={editMode}/>
                    </div>
                    <div>
                        <MyIconButton icon={iconPlus} slot={6} editMode={editMode}/>
                        <MyIconButton icon={iconVolume} editMode={false}/>
                        <MyIconButton icon={iconMinus} slot={7} editMode={editMode}/>
                    </div>
                </div>
            </div>
            <div className="left-bottom">
                <div className="left-bottom-left">
                    <div>
                        <MyIconButton icon={iconPlay} slot={8} editMode={editMode}/>
                        <MyIconButton icon={iconRewind} slot={9} editMode={editMode}/>
                        <MyIconButton icon={iconF1} slot={10} editMode={editMode}/>
                    </div>
                    <div>
                        <MyIconButton icon={iconPause} slot={11} editMode={editMode}/>
                        <MyIconButton icon={iconNext} slot={12} editMode={editMode}/>
                        <MyIconButton icon={iconF2} slot={13} editMode={editMode}/>
                    </div>
                </div>
                <div className="left-bottom-right">
                    <div>
                        <MyIconButton icon={iconSettings} slot={14} editMode={editMode}/>
                        <MyIconButton icon={iconLeft} slot={15} editMode={editMode}/>
                        <MyIconButton icon={iconBack} slot={16} editMode={editMode}/>
                    </div>
                    <div>
                        <MyIconButton icon={iconUp} slot={17} editMode={editMode}/>
                        <MyIconButton icon={iconOk} slot={18} editMode={editMode}/>
                        <MyIconButton icon={iconDown} slot={19} editMode={editMode}/>
                    </div>
                    <div>
                        <MyIconButton icon={iconInput} slot={20} editMode={editMode}/>
                        <MyIconButton icon={iconRight} slot={21} editMode={editMode}/>
                        <MyIconButton icon={iconExit} slot={22} editMode={editMode}/>
                    </div>
                </div>
            </div>
        </div>
    );
});

const RightTvParent = memo(function RightTvParent({editMode}: { editMode: boolean }) {
    const {t} = useLocalization();
    return (
        <div className="right-parent tv">
            <div className="keypad">
                <span>{t('DIAL_CHANNEL')}</span>
                <div className="keypad-row">
                    <MyTextButton slot={23} text="1" editMode={editMode}/>
                    <MyTextButton slot={24} text="2" editMode={editMode}/>
                    <MyTextButton slot={25} text="3" editMode={editMode}/>
                </div>
                <div className="keypad-row">
                    <MyTextButton slot={26} text="4" editMode={editMode}/>
                    <MyTextButton slot={27} text="5" editMode={editMode}/>
                    <MyTextButton slot={28} text="6" editMode={editMode}/>
                </div>
                <div className="keypad-row">
                    <MyTextButton slot={29} text="7" editMode={editMode}/>
                    <MyTextButton slot={30} text="8" editMode={editMode}/>
                    <MyTextButton slot={31} text="9" editMode={editMode}/>
                </div>
                <div className="keypad-row">
                    <span className="no-style"/>
                    <MyTextButton slot={32} text="0" editMode={editMode}/>
                    <span className="no-style"/>
                </div>
            </div>
        </div>
    );
});

export const MediaScreen = memo(function MediaScreen() {
    const {t} = useLocalization();
    const selectedMedia = useSelector(state => state.ui.mediaScreen.selectedMedia);
    const defaultEditMode = useSelector(state => state.ui.mediaScreen.editMode);

    const dispatch = useDispatch();
    const question = useQuestion();
    const getRelations = useRelations();
    const {success} = useMessage();

    const [editMode, setEditMode] = useState(defaultEditMode || false);
    const [rpc] = useRPC();

    const close = useCallback(() => {
        dispatch(setMediaScreenAction(null));
    }, [dispatch]);

    const edit = useCallback(() => setEditMode(mode => !mode), []);

    const remove = useCallback(() => {
        getRelations(selectedMedia.id, 'media')
            .then(([message, positiveButton]) => {
                const questionMessage = message == null ?
                    t('DO_YOU_WANT_TO_DELETE_THE_CORRESPONDING_REMOTE') :
                    t('THIS_WILL_BE_DELETED_WITH_THIS_MESSAGE__CONTINUE',{name:selectedMedia.name, message});
                question(questionMessage, () => {
                    return rpc.deleteMedia(selectedMedia.id).then(() => {
                        close();
                        success(t('REMOTE_SUCCESSFULLY_DELETED'));
                    });
                }, undefined, positiveButton);
            });
    }, [t, question, close, rpc, selectedMedia, success, getRelations]);

    if (!selectedMedia) {
        return null;
    }

    return (
        <>
            <div className={'media-screen media' + (editMode ? ' edit-mode' : '')}>
                <div className="content-divider">
                    <div className="content-wrapper">
                        <div className="actions">
                            <div className="action" {...onClickHandler(close)}>
                                <img src={iconCancel} alt=''/>
                                <span>{t('CLOSE')}</span>
                            </div>
                            {
                                renderIf(editMode)(() =>
                                    <div className="action" {...onClickHandler(remove)}>
                                        <img src={iconRemove} alt=''/>
                                        <span>{t('DELETE')}</span>
                                    </div>,
                                )
                            }
                            <div className="action" {...onClickHandler(edit)}>
                                <img src={editMode ? iconSave : iconEdit} alt=''/>
                                <span>{editMode ? t('OK') : t('EDIT')}</span>
                            </div>
                        </div>
                        {
                            renderIf(selectedMedia.type_id === MEDIA_TYPE_ID_AIR_CONDITIONER)(
                                () => <LeftAirConditionerParent editMode={editMode}/>,
                                () => <LeftTvParent editMode={editMode}/>,
                            )
                        }
                    </div>
                    <div className="content-wrapper">
                        {
                            renderIf(selectedMedia.type_id === MEDIA_TYPE_ID_AIR_CONDITIONER)(
                                () => <RightAirConditionerParent editMode={editMode}/>,
                                () => <RightTvParent editMode={editMode}/>,
                            )
                        }
                    </div>
                </div>
            </div>
        </>
    );
});
